/* eslint-disable @stylistic/max-len */
import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  Button,
  DeleteCircleContained,
  Input,
  Radio as UikitRadio,
  Select as UikitSelect,
} from '@sgcmpteltd/uikit-react';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_FIT_CONTENT,
} from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import type { ChangeEventHandler, MouseEventHandler } from 'react';
import { useState } from 'react';

import {
  NEW_CARS_LISTING_FUEL_TYPE_ELECTRIC,
} from '@/apis/server/new-car/v1/constants';
import { ExpandContentComponent } from '@/components/expand-content';
import {
  AUTHORISED_DISTRIBUTORS,
  FILTER_TYPE_NAME_DEALER_TYPE,
  FILTER_TYPE_NAME_DRIVE_RANGE,
  FILTER_TYPE_NAME_ENERGY_CONSUPTION,
  FILTER_TYPE_NAME_ENGINE_CAP,
  FILTER_TYPE_NAME_FUEL_CONSUMPTION,
  FILTER_TYPE_NAME_FUEL_TYPE,
  PARALLEL_IMPORTERS,
} from '@/constants/global';

import { AutoCompleteQuickBox } from '../auto-complete';
import {
  CHECKBOX_GROUP_EXPAND_CONTENT_INITIAL_HEIGHT,
  SEARCH_INPUT_PLACEHOLDER,
} from '../constants';
import type {
  FilterCheckbox,
  FilterChoice,
  FilterRange,
  FilterSelect,
  FilterValue,
} from '../filter-label-list/types';
import { isFilterCheckbox } from '../filter-label-list/utils';
import { vehicleTypeSelectOptionConfigs } from '../search-bar/configs';
import { Accordion } from './accordion';
import {
  defaultCheckboxValue,
  defaultChoiceValue,
  defaultRangeValue,
  defaultSelectValue,
  inputComponents,
} from './configs';
import type { CheckboxValue } from './input/checkbox-group/types';
import styles from './styles.module.scss';
import type { Category, Component } from './types';
import {
  isCategoryCheckbox,
  isCategoryChoice,
  isCategoryConditionSelect,
  isCategoryRange,
  isCategorySelect,
} from './utils';

export const FilterModal: Component = ({
  brandList,
  searchValue,
  carMakeValue,
  setSearchValue,
  availableYear,
  selectValues,
  setSelectValues,
  rangeValues,
  setRangeValues,
  choiceValues,
  setChoiceValues,
  checkboxValues,
  setCheckboxValues,
  defaultFilterValues,
  categories,
  accordionCategories,
  title = 'New Car Filters',
  hasCarMakeSelect = true,
  hasVehicleTypeSingleSelect = true,
  hasAvailableYearSelect = false,
  hasOnlyDealerTypeSelect = false,
  isOpen,
  autoCompleteOptions,
  isAutoCompleteOpen,
  onSearchInputChange,
  onCarMakeChange,
  onAvailableYearChange,
  onClose,
  onClear,
  onSubmit,
  onAutoCompleteBoxOpen,
  onIgnoreSearchInputChange,
}) => {
  const [
    filterValues,
    setFilterValues,
  ] = useState<FilterValue[]>(defaultFilterValues);

  const [isFocused, setIsFocused] = useState(false);

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onSearchInputChange?.(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
    }
  };

  const handleSearchClick = () => {
    onSubmit?.();
  };

  const { isIpadMini } = useScreenDetect();
  const isIpadMiniView = isIpadMini();

  const handleClearClick: MouseEventHandler<HTMLButtonElement> = () => {
    onClear?.();
    setSearchValue('');
    onCarMakeChange?.('');
    setSelectValues(defaultSelectValue({ query: {} }));
    setRangeValues(defaultRangeValue({ query: {} }));
    setChoiceValues(defaultChoiceValue({ query: {} }));
    setCheckboxValues(defaultCheckboxValue({ query: {} }));
    setFilterValues?.([]);
  };

  const handleClose = () => {
    window.history.pushState(null, '', ' ');
    onClose?.();
  };

  const handleCheckboxChange = (v: FilterCheckbox) => {
    const index = filterValues
      .findIndex((val) => {
        if (isFilterCheckbox(val)) {
          return val.name === v.name && val.checkboxName === v.checkboxName;
        }
        return false;
      });

    setCheckboxValues({
      ...checkboxValues,
      [v.name]: {
        name: v.name,
        type: 'checkbox',
        values: {
          ...checkboxValues[v.name].values,
          [v.checkboxName]: {
            name: v.checkboxName,
            value: v.checkboxValue,
            checked: v.isChecked,
          },
        },
      },
    });

    if (index === -1) { // add
      setFilterValues([v, ...filterValues]);
    } else { // remove
      setFilterValues(filterValues.filter((val) => {
        if (isFilterCheckbox(val)) {
          return !((val.name === v.name
            && val.checkboxValue === v.checkboxValue));
        }
        return true;
      }));
    }
  };

  const [isExpanded, setIsExpanded] = useState(false);

  const handleAllCheckboxChange = (v: Category<'checkbox'> & {
    values: Record<string, CheckboxValue>;
  }) => {
    setCheckboxValues({
      ...checkboxValues,
      [v.name]: v,
    });

    setIsExpanded(!isExpanded);

    // remove and add
    if (Object.values(v.values)[0].checked) {
      setFilterValues([...Object.values(v.values).map(value => ({
        name: v.name,
        type: 'checkbox',
        checkboxName: value.name,
        checkboxValue: value.value,
        isChecked: value.checked,
      }) as FilterCheckbox), ...filterValues.filter(val => val.name !== v.name)]);
    } else {
      // only remove
      setFilterValues([...filterValues.filter(val => val.name !== v.name)]);
    }
  };

  const handlePartialCheckboxChange = (v: Category<'checkbox'> & {
    values: Record<string, CheckboxValue>;
  }) => {
    setCheckboxValues({
      ...checkboxValues,
      [v.name]: v,
    });
  };

  const handleChoiceChange = (v: FilterChoice) => {
    const index = filterValues
      .findIndex(val => val.name === v.name);

    if (v.optionValue.value === '') { // reset
      setFilterValues(
        filterValues
          .filter(val => !(val.name === v.name)),
      );
      setChoiceValues({
        ...choiceValues,
        [v.name]: {
          name: v.name,
          type: 'choice',
          optionValue: {
            text: '',
            value: '',
          },
          unit: v.unit,
          inputValue: '',
        },
      });
      return;
    }

    if (index === -1 && v.inputValue) { // add
      setFilterValues([v, ...filterValues]);
    } else { // update
      setFilterValues(filterValues.map(val => (
        (val.name === v.name) ? v : val
      )));
    }

    setChoiceValues({
      ...choiceValues,
      [v.name]: {
        name: v.name,
        type: 'choice',
        unit: v.unit,
        optionValue: v.optionValue,
        inputValue: v.inputValue,
      },
    });
  };

  const handleSelectChange = (v: FilterSelect) => {
    const index = filterValues
      .findIndex(val => val.name === v.name);

    if (v.optionValue.value === '') { // reset
      setFilterValues(
        filterValues
          .filter(val => !(val.name === v.name)),
      );
    } else if (index === -1) { // add
      setFilterValues([v, ...filterValues]);
    } else { // update
      setFilterValues(filterValues.map(val => (
        (val.name === v.name) ? v : val
      )));
    }

    if (v.name === FILTER_TYPE_NAME_FUEL_TYPE) {
      setSelectValues({
        ...selectValues,
        [v.name]: {
          name: v.name,
          type: 'select',
          optionValue: v.optionValue,
        },
        [FILTER_TYPE_NAME_ENGINE_CAP]: {
          ...selectValues[FILTER_TYPE_NAME_ENGINE_CAP],
          optionValue: {
            text: '',
            value: '',
          },
        },
      });
    } else {
      setSelectValues({
        ...selectValues,
        [v.name]: {
          name: v.name,
          type: 'select',
          optionValue: v.optionValue,
        },
      });
    }

    if (v.name === FILTER_TYPE_NAME_FUEL_TYPE) {
      setRangeValues({
        ...rangeValues,
        [FILTER_TYPE_NAME_DRIVE_RANGE]: {
          ...rangeValues[FILTER_TYPE_NAME_DRIVE_RANGE],
          lowerValue: {
            text: 'Lowest',
            value: '',
          },
          upperValue: {
            text: 'Highest',
            value: '',
          },
        },
      });
      setChoiceValues({
        ...choiceValues,
        [FILTER_TYPE_NAME_FUEL_CONSUMPTION]: {
          ...choiceValues[FILTER_TYPE_NAME_FUEL_CONSUMPTION],
          optionValue: {
            text: '',
            value: '',
          },
          unit: {
            type: 'suffix',
            value: 'km/L',
          },
          inputValue: '',
        },
        [FILTER_TYPE_NAME_ENERGY_CONSUPTION]: {
          ...choiceValues[FILTER_TYPE_NAME_ENERGY_CONSUPTION],
          optionValue: {
            text: '',
            value: '',
          },
          unit: {
            type: 'suffix',
            value: 'km/L',
          },
          inputValue: '',
        },
      });
    }
  };

  const handleRangeChange = (v: FilterRange) => {
    const index = filterValues
      .findIndex(val => val.name === v.name);

    if (v.lowerValue.value === '' && v.upperValue.value === '') { // reset
      setFilterValues(
        filterValues
          .filter(val => !(val.name === v.name)),
      );
    } else if (index === -1) { // add
      setFilterValues([v, ...filterValues]);
    } else { // update
      setFilterValues(filterValues.map(val => (
        (val.name === v.name) ? v : val
      )));
    }

    setRangeValues({
      ...rangeValues,
      [v.name]: {
        name: v.name,
        type: 'range',
        lowerValue: v.lowerValue,
        upperValue: v.upperValue,
      },
    });
  };

  const handleCarMakeChange = (value: string | number) => {
    onCarMakeChange?.(value.toString());
    const index = filterValues
      .findIndex(val => val.name === 'Car Make');

    if (value === '') { // reset
      setFilterValues(
        filterValues
          .filter(val => !(val.name === 'Car Make')),
      );
    } else if (index === -1) { // add
      setFilterValues([{
        name: 'Car Make',
        type: 'select',
        optionValue: {
          text: value.toString(),
          value,
        },
      }, ...filterValues]);
    } else { // update
      setFilterValues(filterValues.map(val => (
        (val.name === 'Car Make')
          ? {
            name: 'Car Make',
            type: 'select',
            optionValue: {
              text: value.toString(),
              value,
            },
          }
          : val
      )));
    }
  };

  const handleAvailableYearChange = (value: string | number) => {
    onAvailableYearChange?.(value.toString());
    const index = filterValues
      .findIndex(val => val.name === 'Available Year');

    if (value === '') { // reset
      setFilterValues(
        filterValues
          .filter(val => !(val.name === 'Available Year')),
      );
    } else if (index === -1) { // add
      setFilterValues([{
        name: 'Available Year',
        type: 'select',
        optionValue: {
          text: value.toString(),
          value,
        },
      }, ...filterValues]);
    } else { // update
      setFilterValues(filterValues.map(val => (
        (val.name === 'Available Year')
          ? {
            name: 'Available Year',
            type: 'select',
            optionValue: {
              text: value.toString(),
              value,
            },
          }
          : val
      )));
    }
  };

  const handleRadioChange: (
    name: string,
    text: string,
    value: string,
  ) => ChangeEventHandler<HTMLInputElement> = (
    name,
    text,
    value,
  ) => () => {
    handleSelectChange({
      name,
      type: 'select',
      optionValue: {
        text,
        value,
      },
    });
  };

  const handleVehicleTypeSelectChange = (value: string | number) => {
    const newValues = { ...checkboxValues['Vehicle Type'].values };

    for (const vehicleType in newValues) {
      if (Object.hasOwn(newValues, vehicleType)) {
        newValues[vehicleType].checked = false;
      }
    }

    setCheckboxValues({
      ...checkboxValues,
      'Vehicle Type': {
        name: 'Vehicle Type',
        type: 'checkbox',
        values: {
          ...newValues,
          [vehicleTypeSelectOptionConfigs.optionList
            .find(option => option.value === value)?.text ?? '']: {
            name: vehicleTypeSelectOptionConfigs.optionList
              .find(option => option.value === value)?.text ?? '',
            value,
            checked: true,
          },
        },
      },
    });
  };

  const handleSearchInputChangeFromAutoComplete = (optionValue: string) => {
    onSearchInputChange?.(optionValue);
    onAutoCompleteBoxOpen?.(false);
  };

  return (
    <Modal
      className={cx(
        styles.containerModal,
        styles.filterPopup,
      )}
      contentClassName={cx(styles.modal, styles.hasNoPadding)}
      headerClassName={cx(styles.popupHeaderClassName, styles.hasPadding)}
      isOpen={isOpen}
      title={null}
      onClose={handleClose}>
      <div className={styles.containerPopupInfo}>
        <div className={styles.textNewCarFilter}>
          {title}
        </div>
        <form
          className={styles.containerSearchInput}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchClick();
          }}>
          <div className={styles.containerInput}>
            <Input
              className={cx(
                styles.inputSearch,
                styles.popupInput,
                styles.isRound,
              )}
              id="advanced-search-input"
              placeholder={SEARCH_INPUT_PLACEHOLDER}
              value={searchValue}
              onBlur={() => setIsFocused(false)}
              onChange={handleSearchChange}
              onFocus={() => setIsFocused(true)}
              onKeyDown={handleSearchKeyDown} />
            {searchValue && isFocused && (
              <button
                aria-label="Clear search"
                className={styles.btnClear}
                onClick={() => setSearchValue('')}
                onMouseDown={e => e.preventDefault()}>
                <DeleteCircleContained size={20} />
              </button>
            )}
          </div>
          {isAutoCompleteOpen
          && autoCompleteOptions
          && (
            <AutoCompleteQuickBox
              autoCompleteOptions={autoCompleteOptions}
              promotionOptions={null}
              onAutoCompleteOptionClick={handleSearchInputChangeFromAutoComplete}
              onIgnoreSearchInputChange={onIgnoreSearchInputChange} />
          )}
          <button
            aria-label="Search"
            style={{ display: 'none' }}
            type="submit">
            submit
          </button>
        </form>
        {hasCarMakeSelect && (
          <div className={styles.containerCarMake}>
            <div className={styles.textName}>Car Make</div>
            <UikitSelect
              headerClassName={cx(styles.headerClassName, styles.isMedia, styles.isSmall, carMakeValue ? styles.activeInput : '')}
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuClassName={cx(styles.menuClassName, styles.menuHeight)}
              optionList={brandList.map(value => ({
                text: value,
                value,
              }))}
              placeholder={{
                text: 'Any',
              }}
              value={carMakeValue}
              onChange={handleCarMakeChange} />
          </div>
        )}
        {hasAvailableYearSelect && (
          <div className={styles.containerCarMake}>
            <div className={styles.textName}>Available Year</div>
            <UikitSelect
              headerClassName={cx(styles.headerClassName, styles.isMedia, styles.isSmall, availableYear ? styles.activeInput : '')}
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuClassName={cx(styles.menuClassName, styles.menuHeight)}
              optionList={Array.from({
                length: new Date().getFullYear() - 2001,
              }, (_, i) => (new Date().getFullYear() - i).toString())
                .map(value => ({
                  text: value,
                  value,
                }))}
              placeholder={{
                text: 'Any',
              }}
              value={availableYear}
              onChange={handleAvailableYearChange} />
          </div>
        )}
        {hasVehicleTypeSingleSelect && (
          <div className={styles.containerCarMake}>
            <div className={styles.textName}>Vehicle Type</div>
            <UikitSelect
              headerClassName={cx(
                styles.headerClassName,
                styles.isMedia,
                styles.isSmall,
                Object.values(checkboxValues['Vehicle Type'].values)
                  .find(type => type.checked)?.value as (string | number)
                  ? styles.activeInput
                  : '',
              )}
              menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
              menuClassName={cx(styles.menuClassName, styles.menuHeight)}
              menuFit={MENU_FIT_CONTENT}
              optionList={vehicleTypeSelectOptionConfigs.optionList}
              placeholder={{
                text: 'Any',
                isOptionHidden: true,
              }}
              value={
                Object.values(checkboxValues['Vehicle Type'].values)
                  .find(type => type.checked)?.value as (string | number) ?? ''
              }
              onChange={handleVehicleTypeSelectChange} />
          </div>
        )}
        {categories.map((category) => {
          if (isCategoryCheckbox(category)) {
            const CheckboxGroup = inputComponents[category.type];
            const { name, checkboxOptions, allCheckbox } = category;

            return (
              <div
                key={name}
                className={styles.containerCategory}>
                <div className={styles.textName}>
                  {name}
                </div>
                { isIpadMiniView
                  ? (
                    <CheckboxGroup
                      allCheckbox={allCheckbox}
                      checkboxOptions={checkboxOptions}
                      name={name}
                      values={checkboxValues[name].values}
                      onAllCheckboxChange={handleAllCheckboxChange}
                      onChange={handleCheckboxChange}
                      onPartialCheckboxChange={handlePartialCheckboxChange} />
                  )
                  : (
                    <ExpandContentComponent
                      height={CHECKBOX_GROUP_EXPAND_CONTENT_INITIAL_HEIGHT}
                      isExpanded={isExpanded}>
                      <CheckboxGroup
                        allCheckbox={allCheckbox}
                        checkboxOptions={checkboxOptions}
                        name={name}
                        values={checkboxValues[name].values}
                        onAllCheckboxChange={handleAllCheckboxChange}
                        onChange={handleCheckboxChange}
                        onPartialCheckboxChange={handlePartialCheckboxChange} />
                    </ExpandContentComponent>
                  )}
              </div>
            );
          }

          if (isCategoryRange(category)) {
            const Range = inputComponents[category.type];
            const { name, lowerSelectOption, upperSelectOption } = category;

            return (
              <div
                key={name}
                className={styles.containerCategory}>
                <div className={styles.textName}>
                  {name}
                </div>
                <Range
                  lowerSelectOption={lowerSelectOption}
                  lowerValue={rangeValues[name].lowerValue}
                  name={name}
                  upperSelectOption={upperSelectOption}
                  upperValue={rangeValues[name].upperValue}
                  onChange={handleRangeChange} />
              </div>
            );
          }
          return null;
        })}
        {hasOnlyDealerTypeSelect && (
          <div className={styles.containerCategoryRadio}>
            <div className={styles.textName}>Dealer Type</div>
            <div className={styles.containerRadios}>
              <UikitRadio
                checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === ''}
                label="Any"
                name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
                value=""
                onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Any', '')} />
              <UikitRadio
                checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === AUTHORISED_DISTRIBUTORS}
                label="Authorised Distributor"
                name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
                value="a"
                onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Authorised Distributor', AUTHORISED_DISTRIBUTORS)} />
              <UikitRadio
                checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === PARALLEL_IMPORTERS}
                label="Parallel Importer"
                name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
                value="p"
                onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Parallel Importer', PARALLEL_IMPORTERS)} />
            </div>
          </div>
        )}
        {/* PENDING UNTIL LISTING RELEASE */}
        {/* <div className={styles.containerCategoryRadio}>
          <div className={styles.textName}>Category</div>
          <div className={styles.textCategoryName}>Dealer</div>
          <div className={styles.containerRadios}>
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === ''}
              label="Any"
              name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
              value=""
              onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Any', '')} />
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === 'a'}
              label="Authorised Distributor"
              name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
              value="a"
              onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Authorised Distributor', 'a')} />
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].optionValue.value === 'p'}
              label="Parallel Importer"
              name={selectValues[FILTER_TYPE_NAME_DEALER_TYPE].name}
              value="p"
              onChange={handleRadioChange(FILTER_TYPE_NAME_DEALER_TYPE, 'Parallel Importer', 'p')} />
          </div>
          <div className={styles.textCategoryName}>COE</div>
          <div className={styles.containerRadios}>
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_COE_TYPE].optionValue.value === ''}
              label="Any"
              name={selectValues[FILTER_TYPE_NAME_COE_TYPE].name}
              value=""
              onChange={handleRadioChange(FILTER_TYPE_NAME_COE_TYPE, 'Any', '')} />
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_COE_TYPE].optionValue.value === 'a'}
              label="Cat A COE"
              name={selectValues[FILTER_TYPE_NAME_COE_TYPE].name}
              value="a"
              onChange={handleRadioChange(FILTER_TYPE_NAME_COE_TYPE, 'Cat A COE', 'a')} />
            <UikitRadio
              checked={selectValues[FILTER_TYPE_NAME_COE_TYPE].optionValue.value === 'b'}
              label="Cat B COE"
              name={selectValues[FILTER_TYPE_NAME_COE_TYPE].name}
              value="b"
              onChange={handleRadioChange(FILTER_TYPE_NAME_COE_TYPE, 'Cat B COE', 'b')} />
          </div>
        </div> */}
        {accordionCategories.map((category, index, array) => {
          if (isCategoryConditionSelect(category)) {
            const ConditionSelect = inputComponents[category.type];
            const {
              name, selectOption, condition, extraInputs,
            } = category;

            return (
              <Accordion
                key={name}
                defaultIsOpen={!!selectValues[name].optionValue.value}
                title={name}>
                <ConditionSelect
                  condition={condition}
                  defaultConditionIndex={
                    condition(selectValues[name].optionValue.value)
                  }
                  extraInputs={extraInputs}
                  extraOnChange={[
                    [],
                    [handleChoiceChange],
                    [handleRangeChange, handleChoiceChange],
                  ]}
                  extraValues={[
                    [],
                    [{
                      optionValue: {
                        text: choiceValues[FILTER_TYPE_NAME_FUEL_CONSUMPTION].optionValue.text,
                        value: choiceValues[FILTER_TYPE_NAME_FUEL_CONSUMPTION].optionValue.value,
                      },
                      inputValue: choiceValues[FILTER_TYPE_NAME_FUEL_CONSUMPTION].inputValue,
                    }],
                    [
                      {
                        lowerValue: {
                          text: rangeValues[FILTER_TYPE_NAME_DRIVE_RANGE].lowerValue.text,
                          value: rangeValues[FILTER_TYPE_NAME_DRIVE_RANGE].lowerValue.value,
                        },
                        upperValue: {
                          text: rangeValues[FILTER_TYPE_NAME_DRIVE_RANGE].upperValue.text,
                          value: rangeValues[FILTER_TYPE_NAME_DRIVE_RANGE].upperValue.value,
                        },
                      },
                      {
                        optionValue: {
                          text:
                          choiceValues[FILTER_TYPE_NAME_ENERGY_CONSUPTION].optionValue.text,
                          value:
                          choiceValues[FILTER_TYPE_NAME_ENERGY_CONSUPTION].optionValue.value,
                        },
                        inputValue: choiceValues[FILTER_TYPE_NAME_ENERGY_CONSUPTION].inputValue,
                      },
                    ],
                  ]}
                  name={name}
                  optionValue={selectValues[name].optionValue}
                  selectOption={selectOption}
                  onChange={handleSelectChange} />
              </Accordion>
            );
          }

          if (isCategoryCheckbox(category)) {
            const CheckboxGroup = inputComponents[category.type];
            const { name, checkboxOptions, allCheckbox } = category;

            return (
              <Accordion
                key={name}
                defaultIsOpen={
                  Object
                    .values(checkboxValues[name].values)
                    .findIndex(v => v.checked) !== -1
                }
                title={name}>
                <CheckboxGroup
                  allCheckbox={allCheckbox}
                  checkboxOptions={checkboxOptions}
                  name={name}
                  values={checkboxValues[name].values}
                  onAllCheckboxChange={handleAllCheckboxChange}
                  onChange={handleCheckboxChange} />
              </Accordion>
            );
          }

          if (isCategoryChoice(category)) {
            const Choice = inputComponents[category.type];
            const { name, selectOption, inputOption } = category;

            if (name === FILTER_TYPE_NAME_ENERGY_CONSUPTION && selectValues[FILTER_TYPE_NAME_FUEL_TYPE].optionValue.value !== NEW_CARS_LISTING_FUEL_TYPE_ELECTRIC) {
              return null;
            }

            if (name === FILTER_TYPE_NAME_FUEL_CONSUMPTION && (selectValues[FILTER_TYPE_NAME_FUEL_TYPE].optionValue.value === NEW_CARS_LISTING_FUEL_TYPE_ELECTRIC || selectValues[FILTER_TYPE_NAME_FUEL_TYPE].optionValue.value === '')) {
              return null;
            }
            return (
              <Accordion
                key={name}
                defaultIsOpen={!!choiceValues[name].inputValue}
                suffixText={`${choiceValues[name].optionValue.text || (selectOption.placeholder?.text ?? 'Any')} ${(choiceValues[name].inputValue && choiceValues[name].unit?.type === 'prefix') ? choiceValues[name].unit.value : ''}${choiceValues[name].inputValue === '' ? choiceValues[name].inputValue : Number(choiceValues[name].inputValue).toLocaleString('en-US')} ${(choiceValues[name].inputValue && choiceValues[name].unit?.type === 'suffix') ? choiceValues[name].unit.value : ''}`}
                title={name}>
                <Choice
                  inputOption={inputOption}
                  inputValue={choiceValues[name].inputValue}
                  isLast={index === array.length - 1}
                  name={name}
                  optionValue={choiceValues[name].optionValue}
                  selectOption={selectOption}
                  onChange={handleChoiceChange} />
              </Accordion>
            );
          }

          if (isCategorySelect(category)) {
            const Select = inputComponents[category.type];
            const { name, selectOption } = category;

            if (name === FILTER_TYPE_NAME_ENGINE_CAP && selectValues[FILTER_TYPE_NAME_FUEL_TYPE].optionValue.value === NEW_CARS_LISTING_FUEL_TYPE_ELECTRIC) {
              return null;
            }

            return (
              <Accordion
                key={name}
                defaultIsOpen={!!selectValues[name].optionValue.value}
                suffixText={
                  selectValues[name].optionValue.text || (selectOption.placeholder?.text ?? 'Any')
                }
                title={name}>
                <Select
                  name={name}
                  selectOption={selectOption}
                  value={selectValues[name].optionValue}
                  onChange={handleSelectChange} />
              </Accordion>
            );
          }

          if (isCategoryRange(category)) {
            const Range = inputComponents[category.type];
            const { name, lowerSelectOption, upperSelectOption } = category;

            let preview: string;

            if (rangeValues[name].lowerValue.value && rangeValues[name].upperValue.value) {
              preview = `${rangeValues[name].lowerValue.text} to ${rangeValues[name].upperValue.text}`;
            } else if (rangeValues[name].lowerValue.value && !rangeValues[name].upperValue.value) {
              preview = `More than ${rangeValues[name].lowerValue.text}`;
            } else if (!rangeValues[name].lowerValue.value && rangeValues[name].upperValue.value) {
              preview = `Less than ${rangeValues[name].upperValue.text}`;
            } else {
              preview = 'Any';
            }

            if (name === FILTER_TYPE_NAME_DRIVE_RANGE && selectValues[FILTER_TYPE_NAME_FUEL_TYPE].optionValue.value === NEW_CARS_LISTING_FUEL_TYPE_ELECTRIC) {
              return (
                <div
                  key={name}
                  className={styles.containerAccordion}>
                  <Accordion
                    key={name}
                    defaultIsOpen={!!(
                      rangeValues[name].lowerValue.value
                      || rangeValues[name].upperValue.value
                    )}
                    suffixText={preview}
                    title={name}>
                    <div className={styles.containerRange}>
                      <Range
                        lowerSelectOption={lowerSelectOption}
                        lowerValue={rangeValues[name].lowerValue}
                        name={name}
                        upperSelectOption={upperSelectOption}
                        upperValue={rangeValues[name].upperValue}
                        onChange={handleRangeChange} />
                    </div>
                  </Accordion>
                </div>
              );
            }

            return null;
          }
          return null;
        })}
      </div>
      <div className={styles.containerStickyButton}>
        <div className={styles.containerButtons}>
          <button
            className={styles.clearButton}
            onClick={handleClearClick}>
            Clear
          </button>
          <Button
            className={cx(
              styles.searchButton,
              styles.isRound,
            )}
            variant="contained"
            onClick={handleSearchClick}>
            Search
          </Button>
        </div>
      </div>
    </Modal>
  );
};
